import React from 'react';
import { NavLink } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export interface NavItemProps {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Collapsed rail: hides the label and surfaces it as a tooltip instead. */
  collapsed?: boolean;
  onNavigate?: () => void;
}

/** NavLink sets aria-current itself; `end` keeps /documents dark on its children. */
const NavItem = React.forwardRef<HTMLAnchorElement, NavItemProps>(
  ({ to, label, icon: Icon, collapsed = false, onNavigate }, ref) => {
    const link = (
      <NavLink
        ref={ref}
        to={to}
        end
        onClick={onNavigate}
        aria-label={collapsed ? label : undefined}
        className={cn(
          'group flex h-9 items-center rounded-md text-sm font-medium no-underline outline-hidden',
          'text-muted-foreground transition-colors duration-150 ease-shell',
          'hover:bg-accent/60 hover:text-foreground',
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-card',
          'aria-[current=page]:bg-accent aria-[current=page]:text-accent-foreground',
          collapsed ? 'justify-center' : 'gap-3 px-2.5',
        )}
      >
        <Icon
          className="size-[18px] shrink-0 transition-colors group-aria-[current=page]:text-primary"
          strokeWidth={1.75}
        />
        {!collapsed && <span className="truncate">{label}</span>}
      </NavLink>
    );

    if (!collapsed) return link;

    return (
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right" sideOffset={12} className="revy-ui">
          {label}
        </TooltipContent>
      </Tooltip>
    );
  },
);
NavItem.displayName = 'NavItem';

export default NavItem;
